import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";
import * as Sentry from "@sentry/react";

import Button, { SecondaryButton } from "./Button";
import Link from "./Link";
import useCopyToClipboard from "../hooks/useCopyToClipboard";
import { trackEvent } from "../utils/analyticsUtils";
import { TRUSTPILOT } from "../constants";
import { REFERRAL_SHARE_RATINGS_LINE } from "../utils/referralUtils";

// The claim the shared message makes, with "Trustpilot" linked to the reviews that back it
export function RatedOnTrustpilot() {
  return (
    <>
      Juno are rated {TRUSTPILOT.rating} out of 5 on{" "}
      <Link href={TRUSTPILOT.reviewUrl} target="_blank" rel="noopener noreferrer">
        Trustpilot
      </Link>{" "}
      from {TRUSTPILOT.reviewCount} client reviews.
    </>
  );
}

// Tags a share with where it came from and the channel it went out on, so GA
// can split them. The code in the path stays the attribution key, so the plain
// URL is what the client sees and copies.
const parseUrl = (value) => {
  try {
    return new URL(value);
  } catch {
    return null;
  }
};

// The backend builds the link, so it should always parse. Some dashboards have
// had one that didn't, for a reason not yet found, so the link goes out
// untagged rather than taking the dashboard down.
const withTracking = (referralUrl, utmSource, channel, variant) => {
  const url = parseUrl(referralUrl);
  if (!url) return referralUrl;
  url.searchParams.set("utm_source", utmSource);
  url.searchParams.set("utm_medium", channel);
  url.searchParams.set("utm_campaign", "client_referral");
  url.searchParams.set("utm_content", variant);
  return url.toString();
};

function ShareReferralLink({ referralUrl, shareText, emailSubject, variant, utmSource }) {
  const [copied, copy] = useCopyToClipboard();
  const [showShareLinks, setShowShareLinks] = useState(false);

  // Reports the shape of the link, not the link: the referral code is in it
  useEffect(() => {
    if (parseUrl(referralUrl)) return;
    Sentry.captureMessage("Referral link could not be parsed", {
      level: "warning",
      tags: { action: "ShareReferralLink" },
      extra: {
        type: typeof referralUrl,
        length: referralUrl?.length,
        hasScheme: /^[a-z][a-z0-9+.-]*:/i.test(referralUrl ?? ""),
      },
    });
  }, [referralUrl]);

  const track = (channel) => trackEvent("referral_share", { channel, variant, source: utmSource });
  const trackedUrl = (channel) => withTracking(referralUrl, utmSource, channel, variant);
  // The friend's link comes before the ratings link, so chat apps preview the friend's link
  const message = (channel) => `${shareText} ${trackedUrl(channel)}\n\n${REFERRAL_SHARE_RATINGS_LINE}`;

  const copyLink = async () => {
    track("copy");
    await copy(referralUrl);
  };

  const share = async () => {
    if (typeof navigator.share === "function") {
      track("native");
      try {
        // The link goes in the text rather than as url, which most apps would add after the ratings link
        await navigator.share({ title: emailSubject, text: message("native") });
      } catch (err) {
        // The client closed the share sheet, or the browser refused; nothing to do
      }
      return;
    }
    setShowShareLinks(true);
  };

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(message("whatsapp"))}`;
  const emailHref = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(message("email"))}`;

  return (
    <>
      <LinkRow>
        <ReferralUrl href={referralUrl}>{referralUrl}</ReferralUrl>
        <Actions>
          <Button type="button" small onClick={copyLink}>
            {copied ? "Copied" : "Copy your link"}
          </Button>
          <SecondaryButton type="button" small onClick={share}>
            Share
          </SecondaryButton>
        </Actions>
      </LinkRow>
      {showShareLinks && (
        <ShareLinks aria-label="Share your link">
          <li>
            <Link href={whatsappHref} target="_blank" rel="noreferrer" onClick={() => track("whatsapp")}>
              WhatsApp
            </Link>
          </li>
          <li>
            <Link href={emailHref} onClick={() => track("email")}>
              Email
            </Link>
          </li>
        </ShareLinks>
      )}
    </>
  );
}

ShareReferralLink.propTypes = {
  referralUrl: PropTypes.string.isRequired,
  // The message shared with the link; the link and then the ratings link are appended
  shareText: PropTypes.string.isRequired,
  emailSubject: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(["refer_buyer", "refer_friend"]).isRequired,
  // Where the link is shared from, for utm_source and the share event
  utmSource: PropTypes.oneOf(["client_dashboard", "refer_a_friend_page"]).isRequired,
};

export default ShareReferralLink;

const LinkRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--juno-space-3) var(--juno-space-4);
  margin-bottom: var(--juno-space-3);
`;

const ReferralUrl = styled(Link)`
  font-family: var(--juno-font-family-mono);
  font-size: var(--juno-font-size-body-sm);
  word-break: break-all;
  padding: var(--juno-space-2) var(--juno-space-3);
  border-radius: var(--juno-radius-sm);
  background-color: var(--juno-color-palest-blue);
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--juno-space-2);
`;

const ShareLinks = styled.ul`
  display: flex;
  gap: var(--juno-space-4);
  list-style: none;
  margin: 0 0 var(--juno-space-3) 0;
  padding: 0;
`;
